/**
 * Auth state hook backed by a Zustand store.
 *
 * Why Zustand: guards/shell read `useAuth(s => s.isLoggedIn)`, the Zustand
 * selector signature, and it keeps the auth surface idiomatic with
 * `store/ui.ts` / `store/registrations.ts`. `useSyncExternalStore` would also
 * work but Zustand is already a dependency, so we stay consistent.
 *
 * OIDC-only since the 3-app carve (C5): there is no password `login()` — the
 * Keycloak page owns sign-in (`ensureKeycloakLogin`), so this store only holds
 * the `isLoggedIn` flag and the OIDC-aware `logout()`.
 */

import { create } from 'zustand';
import { isLoggedIn as readIsLoggedIn } from './tokens';
import { keycloakLogout } from './keycloak';

interface AuthState {
  isLoggedIn: boolean;
  logout: () => void;
  /** Re-read tokens and update `isLoggedIn`. Called by `ensureKeycloakLogin()`
   *  after OIDC init plugs the token source; also an escape hatch for tests. */
  refreshLoginState: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  isLoggedIn: readIsLoggedIn(),

  refreshLoginState: () => {
    set({ isLoggedIn: readIsLoggedIn() });
  },

  logout: () => {
    if (typeof window === 'undefined') return;
    // RF-118: NÃO descarte a sessão local aqui. A saída pode ser cancelada pelo
    // usuário no diálogo `beforeunload` (KDS com pedido ativo, EC-012) e a página
    // segue viva — limpar antes deixava o app montado com `isLoggedIn: false`, e o
    // PanelGuard num spinner eterno (`ensureKeycloakLogin()` relatcha na promise já
    // resolvida e nunca reexecuta `refreshLoginState()`).
    //
    // Nada se perde: `keycloakLogout()` navega cross-document, então o store morre
    // com o documento, e todo `main.tsx` já chama `clearTokens()` no boot. O
    // StaffShell sempre deslogou exatamente assim (só `keycloakLogout()`, sem
    // teardown local) e nunca teve este bug.
    //
    // OIDC-only: keycloakLogout() ends the Keycloak SSO cookie and lands on the
    // panel root (`origin/`) — the PanelGuard re-drives login there. It is the
    // ONLY navigator on logout (callers must not race it — see MerchantShell).
    // No-op when there is no KC instance (defensive; every app inits one).
    keycloakLogout(window.location.origin + '/');
  },
}));
